import { useEffect, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { SessionPersistenceService } from '../../services/session';

/**
 * Hook para manejar la persistencia de sesión en recargas de página
 * Funciona con memoryStorage usando solo metadatos de sesión
 */
export const useSessionPersistence = (msalInstance: PublicClientApplication) => {
    const [isSessionRestored, setIsSessionRestored] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const restoreSession = async () => {
            try {
                setIsLoading(true);
                
                // Manejar cualquier resultado de redirección pendiente
                const redirectResponse = await msalInstance.handleRedirectPromise();
                
                if (redirectResponse && redirectResponse.account) {
                    // Usuario acaba de autenticarse via redirect
                    console.log('User authenticated via redirect');
                    SessionPersistenceService.saveSessionMetadata(redirectResponse.account);
                    setIsSessionRestored(true);
                } else {
                    // Intentar restaurar sesión usando metadatos guardados
                    const restored = await SessionPersistenceService.attemptSessionRestore(msalInstance);
                    setIsSessionRestored(restored);
                    
                    if (!restored) {
                        console.log('No valid session found');
                    }
                }
            } catch (error) {
                console.error('Error restoring session:', error);
                setIsSessionRestored(false);
            } finally {
                setIsLoading(false);
            }
        };

        restoreSession();
    }, [msalInstance]);

    return {
        isSessionRestored,
        isLoading
    };
};

/**
 * Hook para manejar la auto-renovación de tokens
 */
export const useTokenAutoRenewal = (msalInstance: PublicClientApplication) => {
    useEffect(() => {
        // Configurar tracking de actividad
        const cleanupActivityTracking = SessionPersistenceService.setupActivityTracking();
        
        const renewalInterval = setInterval(async () => {
            const account = msalInstance.getActiveAccount();
            
            if (account && SessionPersistenceService.hasValidSessionMetadata()) {
                try {
                    // Intentar renovar el token de forma silenciosa
                    await msalInstance.acquireTokenSilent({
                        scopes: ['User.Read'],
                        account,
                        forceRefresh: false
                    });
                    
                    console.log('Token renewed successfully');
                    SessionPersistenceService.updateActivity();
                } catch (error) {
                    console.warn('Token renewal failed:', error);
                    // Si falla la renovación, limpiar metadatos
                    SessionPersistenceService.clearSessionMetadata();
                }
            }
        }, 30 * 60 * 1000); // Renovar cada 30 minutos

        return () => {
            clearInterval(renewalInterval);
            cleanupActivityTracking();
        };
    }, [msalInstance]);
};

/**
 * Hook combinado para manejar persistencia y auto-renovación
 */
export const useAuthPersistence = (msalInstance: PublicClientApplication) => {
    const sessionPersistence = useSessionPersistence(msalInstance);
    useTokenAutoRenewal(msalInstance);
    
    return sessionPersistence;
};